import { Button, Flex, Form, Input, Modal, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { AssetDetailComponent } from '~/app/services/asset/asset.service';
import { useComponentTwinsGroupsColumns } from './hooks/useComponentTwinsGroupsColumns';
import { useComponentTwinsGroupsSection } from './hooks/useComponentTwinsGroupsSection';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

export interface ComponentTwinsGroupsSectionProps {
  organizationId: string;
  assetId: string;
  components: AssetDetailComponent[];
}

export function ComponentTwinsGroupsSection({
  organizationId,
  assetId,
  components,
}: ComponentTwinsGroupsSectionProps) {
  const { t } = useTranslation('assets');
  const section = useComponentTwinsGroupsSection(
    organizationId,
    assetId,
    components,
  );
  const columns = useComponentTwinsGroupsColumns({
    componentsById: section.componentsById,
    canUpdate: section.canUpdate,
    canDelete: section.canDelete,
    onEdit: section.openEditModal,
    onDelete: section.confirmDelete,
  });

  if (!section.canRead) {
    return null;
  }

  return (
    <Flex vertical gap="middle">
      <Flex align="center" justify="space-between">
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('detail.twins.title', 'Component twins groups')}
        </Typography.Title>
        {section.canCreate ? (
          <Button type="primary" onClick={section.openCreateModal}>
            {t('detail.twins.create', 'Create group')}
          </Button>
        ) : null}
      </Flex>

      <EvimoTable
        className="ds-table"
        rowKey="id"
        loading={section.isLoading}
        columns={columns}
        dataSource={section.groups}
        pagination={false}
        locale={{
          emptyText: t(
            'detail.twins.empty',
            'No twins groups defined for this asset.',
          ),
        }}
      />

      <Modal
        title={
          section.editingGroup
            ? t('detail.twins.editModal.title', 'Edit twins group')
            : t('detail.twins.createModal.title', 'Create twins group')
        }
        open={section.modalOpen}
        onCancel={section.closeModal}
        onOk={section.submitModal}
        confirmLoading={section.isSaving}
        okText={t('detail.twins.modal.save', 'Save')}
        cancelText={t('detail.twins.modal.cancel', 'Cancel')}
        destroyOnClose
      >
        <Form form={section.form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="groupName"
            label={t('detail.twins.form.groupName', 'Group name')}
            rules={[
              {
                required: true,
                message: t(
                  'detail.twins.form.groupNameRequired',
                  'Group name is required',
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'detail.twins.form.groupNamePlaceholder',
                'Enter group name',
              )}
            />
          </Form.Item>
          <Form.Item
            name="componentTwinsIds"
            label={t('detail.twins.form.members', 'Twin components')}
            rules={[
              {
                required: true,
                type: 'array',
                min: 2,
                message: t(
                  'detail.twins.form.membersMin',
                  'Select at least two components',
                ),
              },
            ]}
          >
            <Select
              mode="multiple"
              options={section.componentOptions}
              placeholder={t(
                'detail.twins.form.membersPlaceholder',
                'Select twin components',
              )}
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
}
